import { forwardRef, type ButtonHTMLAttributes } from "react"
import { cn } from "../../lib/utils"

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Toggled-on look (for example, an open menu trigger). */
  active?: boolean
}

/**
 * Ghost icon button with a full state model: idle, hover, active (pressed),
 * focus-visible ring, toggled (`active`), and disabled. The visible target is
 * 36px with a 40px hit area via a pseudo-element.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, active, type = "button", ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      data-active={active ? "true" : undefined}
      className={cn(
        // 36px visual, 40px hit area (pseudo-element) — meets the 40px target
        // without changing density; a 4px gap keeps adjacent areas from overlapping.
        "relative inline-flex size-9 items-center justify-center rounded-[10px] text-muted-foreground before:absolute before:-inset-0.5 before:content-['']",
        "transition-[background-color,color,transform] duration-150 ease-out",
        "hover:bg-accent hover:text-accent-foreground",
        "active:scale-[0.96]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:pointer-events-none disabled:opacity-50",
        "data-[active=true]:bg-accent data-[active=true]:text-accent-foreground",
        className,
      )}
      {...rest}
    />
  ),
)

IconButton.displayName = "IconButton"
